/**
 * Monitoring Tab — Admin Client Detail
 * Aggregated system health: pipeline stats, error rates, latency, bridge health, top errors.
 * Auto-refreshes every 30 seconds. CSS-only visualizations (no charting library).
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { useAdminApi } from "@/services/api/admin-api";
import type { MonitoringDashboardDto } from "@/types/api/admin";
import { cx } from "@/utils/cx";

// =============================================================================
// TYPES
// =============================================================================

interface MonitoringTabProps {
    clientId: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const WINDOW_OPTIONS: SelectItemType[] = [
    { id: "15", label: "Last 15 minutes" },
    { id: "60", label: "Last 1 hour" },
    { id: "360", label: "Last 6 hours" },
    { id: "1440", label: "Last 24 hours" },
    { id: "10080", label: "Last 7 days" },
];

const AUTO_REFRESH_MS = 30_000;
const P95_THRESHOLD_MS = 10_000;

// =============================================================================
// HELPERS
// =============================================================================

function StatCard({ label, value, color }: { label: string; value: number; color: "brand" | "warning" | "success" | "error" | "gray" }) {
    return (
        <div className="rounded-lg border border-secondary p-4">
            <p className="text-xs font-medium text-tertiary">{label}</p>
            <div className="mt-1 flex items-center gap-2">
                <span className="text-2xl font-semibold text-primary">{value.toLocaleString()}</span>
                <Badge color={color} size="sm" type="pill-color">
                    {label}
                </Badge>
            </div>
        </div>
    );
}

/**
 * One entry of the connector health array the bridge sends (ConnectorHealthDto).
 * `lastSuccessfulConnectionAt` and `latencyMs` are additive fields — payloads from
 * older bridge builds simply omit them.
 */
interface ConnectorHealthEntry {
    connectorName: string;
    isConnected: boolean;
    lastCheckedAt: string | null;
    errorMessage: string | null;
    lastSuccessfulConnectionAt: string | null;
    latencyMs: number | null;
}

function parseConnectorHealth(json: string): ConnectorHealthEntry[] | null {
    try {
        const parsed: unknown = JSON.parse(json);
        if (!Array.isArray(parsed)) return null;
        return parsed
            .filter((entry): entry is Record<string, unknown> => typeof entry === "object" && entry !== null)
            .map((entry) => ({
                connectorName:
                    typeof entry.connectorName === "string" && entry.connectorName !== "" ? entry.connectorName : "Unknown connector",
                isConnected: entry.isConnected === true,
                lastCheckedAt: typeof entry.lastCheckedAt === "string" ? entry.lastCheckedAt : null,
                errorMessage: typeof entry.errorMessage === "string" ? entry.errorMessage : null,
                lastSuccessfulConnectionAt: typeof entry.lastSuccessfulConnectionAt === "string" ? entry.lastSuccessfulConnectionAt : null,
                latencyMs: typeof entry.latencyMs === "number" ? entry.latencyMs : null,
            }));
    } catch {
        return null;
    }
}

function ConnectorHealthDisplay({ json }: { json: string }) {
    const entries = parseConnectorHealth(json);

    if (!entries) {
        return (
            <div className="border-t border-secondary pt-2">
                <p className="text-xs text-warning-primary">Connector health unavailable (unrecognized payload).</p>
            </div>
        );
    }

    if (entries.length === 0) {
        return (
            <div className="border-t border-secondary pt-2">
                <p className="text-xs text-quaternary">No connectors configured.</p>
            </div>
        );
    }

    return (
        <div className="space-y-2 border-t border-secondary pt-2">
            {entries.map((entry, i) => (
                <div key={`${entry.connectorName}-${i}`}>
                    <div className="flex items-center gap-1.5">
                        <span
                            className={cx(
                                "inline-block size-2 shrink-0 rounded-full",
                                entry.isConnected ? "bg-success-primary" : "bg-error-primary",
                            )}
                        />
                        <span className="min-w-0 truncate text-xs font-medium text-secondary" title={entry.connectorName}>
                            {entry.connectorName}
                        </span>
                        {entry.latencyMs !== null && <span className="shrink-0 text-xs text-quaternary">{formatMs(entry.latencyMs)}</span>}
                    </div>
                    {entry.isConnected ? (
                        entry.lastSuccessfulConnectionAt && (
                            <p className="pl-3.5 text-xs text-quaternary">
                                Last success {new Date(entry.lastSuccessfulConnectionAt).toLocaleTimeString()}
                            </p>
                        )
                    ) : (
                        <div className="pl-3.5">
                            <p className="truncate text-xs text-error-primary" title={entry.errorMessage ?? undefined}>
                                {entry.errorMessage ?? "Unhealthy (no error reported)"}
                            </p>
                            <p className="text-xs text-quaternary">
                                {entry.lastCheckedAt ? `Checked ${new Date(entry.lastCheckedAt).toLocaleTimeString()}` : null}
                                {entry.lastSuccessfulConnectionAt
                                    ? `${entry.lastCheckedAt ? " · " : ""}Last success ${new Date(entry.lastSuccessfulConnectionAt).toLocaleTimeString()}`
                                    : null}
                            </p>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function formatMs(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
}

// =============================================================================
// MONITORING TAB
// =============================================================================

export function MonitoringTab({ clientId }: MonitoringTabProps) {
    const adminApi = useAdminApi();

    const [dashboard, setDashboard] = useState<MonitoringDashboardDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [windowMinutes, setWindowMinutes] = useState<string>("60");
    const [isRequeuing, setIsRequeuing] = useState(false);
    const [requeueResult, setRequeueResult] = useState<string | null>(null);

    // -------------------------------------------------------------------------
    // Data fetching
    // -------------------------------------------------------------------------

    const fetchDashboard = useCallback(async () => {
        setError(null);
        try {
            const data = await adminApi.getMonitoringDashboard(clientId, Number(windowMinutes));
            setDashboard(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load monitoring data.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, windowMinutes]);

    // Initial fetch + re-fetch on window change
    useEffect(() => {
        setIsLoading(true);
        fetchDashboard();
    }, [fetchDashboard]);

    // Auto-refresh every 30s (no loading spinner)
    useEffect(() => {
        const interval = setInterval(fetchDashboard, AUTO_REFRESH_MS);
        return () => clearInterval(interval);
    }, [fetchDashboard]);

    // -------------------------------------------------------------------------
    // Bulk requeue
    // -------------------------------------------------------------------------

    const handleBulkRequeue = useCallback(async () => {
        setIsRequeuing(true);
        setRequeueResult(null);
        try {
            const result = await adminApi.bulkRequeueClientJobs(clientId, { status: "Failed" });
            setRequeueResult(`${result.requeuedCount} requeued, ${result.skippedCount} skipped`);
            await fetchDashboard();
        } catch (err) {
            setRequeueResult(err instanceof Error ? err.message : "Requeue failed.");
        } finally {
            setIsRequeuing(false);
        }
    }, [adminApi, clientId, fetchDashboard]);

    // -------------------------------------------------------------------------
    // Loading / Error states
    // -------------------------------------------------------------------------

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <LoadingIndicator size="sm" label="Loading monitoring data..." />
            </div>
        );
    }

    if (error || !dashboard) {
        return <p className="text-sm text-error-primary">{error || "No monitoring data available."}</p>;
    }

    const { pipelineHealth, errorRateTimeSeries, latencyPercentiles, bridgeHealth, topErrors, stuckJobs } = dashboard;
    const maxErrorRate = Math.max(...errorRateTimeSeries.map((b) => b.errorRate), 1);

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <div className="space-y-6">
            {/* Header + Time Window Selector */}
            <section className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Monitoring</h3>
                    <p className="mt-1 text-sm text-tertiary">System health and pipeline metrics. Auto-refreshes every 30s.</p>
                </div>
                <div className="w-52">
                    <Select
                        label="Time window"
                        items={WINDOW_OPTIONS}
                        selectedKey={windowMinutes}
                        onSelectionChange={(key) => setWindowMinutes(String(key ?? "60"))}
                        size="sm"
                    >
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
            </section>

            {/* Stuck Jobs Alert Banner */}
            {stuckJobs.count > 0 && (
                <div className="rounded-lg border border-error-primary bg-error-primary/10 px-4 py-3">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="size-5 shrink-0 text-error-primary" />
                        <span className="text-sm font-medium text-error-primary">
                            {stuckJobs.count} stuck job{stuckJobs.count !== 1 ? "s" : ""} detected
                        </span>
                        <span className="text-sm text-error-primary">&mdash; oldest: {Math.round(stuckJobs.oldestJobAgeMinutes)} min ago</span>
                    </div>
                    {stuckJobs.sampleJobIds.length > 0 && (
                        <p className="mt-1 text-xs text-tertiary">Sample IDs: {stuckJobs.sampleJobIds.join(", ")}</p>
                    )}
                </div>
            )}

            {/* Pipeline Health Cards */}
            <section>
                <h4 className="mb-3 text-sm font-medium text-secondary">Pipeline Health</h4>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    <StatCard label="Queued" value={pipelineHealth.queuedCount} color="brand" />
                    <StatCard label="Processing" value={pipelineHealth.processingCount} color="warning" />
                    <StatCard label="Completed" value={pipelineHealth.completedCount} color="success" />
                    <StatCard label="Failed" value={pipelineHealth.failedCount} color="error" />
                    <StatCard label="Canceled" value={pipelineHealth.canceledCount} color="gray" />
                    <div className="rounded-lg border border-secondary p-4">
                        <p className="text-xs font-medium text-tertiary">Success Rate</p>
                        <div className="mt-1 flex items-center gap-2">
                            <span className="text-2xl font-semibold text-primary">{pipelineHealth.successRate.toFixed(1)}%</span>
                            <Badge
                                color={pipelineHealth.successRate >= 95 ? "success" : pipelineHealth.successRate >= 90 ? "warning" : "error"}
                                size="sm"
                                type="pill-color"
                            >
                                {pipelineHealth.successRate >= 95 ? "Healthy" : pipelineHealth.successRate >= 90 ? "Degraded" : "Critical"}
                            </Badge>
                        </div>
                        {pipelineHealth.retryExhaustedCount > 0 && (
                            <p className="mt-1 text-xs text-error-primary">{pipelineHealth.retryExhaustedCount} retry-exhausted</p>
                        )}
                    </div>
                </div>
            </section>

            {/* Error Rate Timeline */}
            <section>
                <h4 className="mb-3 text-sm font-medium text-secondary">Error Rate Timeline</h4>
                {errorRateTimeSeries.length === 0 ? (
                    <p className="text-sm text-quaternary">No data for this time window.</p>
                ) : (
                    <div className="rounded-lg border border-secondary p-4">
                        <div className="flex items-end gap-px" style={{ height: "120px" }}>
                            {errorRateTimeSeries.map((bucket, i) => {
                                const heightPct = (bucket.errorRate / maxErrorRate) * 100;
                                const isHigh = bucket.errorRate > 5;
                                return (
                                    <div key={i} className="group relative flex-1" style={{ height: "100%" }}>
                                        <div
                                            className={cx(
                                                "absolute bottom-0 w-full rounded-t transition-colors",
                                                isHigh ? "bg-error-primary" : "bg-brand-primary",
                                                "min-h-[2px]",
                                            )}
                                            style={{ height: `${Math.max(heightPct, 2)}%` }}
                                        />
                                        {/* Tooltip */}
                                        <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-primary px-2 py-1 text-xs text-white shadow group-hover:block">
                                            {new Date(bucket.bucketStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                            <br />
                                            {bucket.failedJobs}/{bucket.totalJobs} failed ({bucket.errorRate.toFixed(1)}%)
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-2 flex justify-between text-xs text-quaternary">
                            <span>
                                {new Date(errorRateTimeSeries[0].bucketStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                            <span>
                                {new Date(errorRateTimeSeries[errorRateTimeSeries.length - 1].bucketStart).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </span>
                        </div>
                    </div>
                )}
            </section>

            {/* Latency Table */}
            <section>
                <h4 className="mb-3 text-sm font-medium text-secondary">Latency Percentiles</h4>
                {latencyPercentiles.length === 0 ? (
                    <p className="text-sm text-quaternary">No latency data available.</p>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-secondary">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-secondary bg-secondary_subtle">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-secondary">Job Type</th>
                                    <th className="px-4 py-3 font-medium text-secondary">P50</th>
                                    <th className="px-4 py-3 font-medium text-secondary">P95</th>
                                    <th className="px-4 py-3 font-medium text-secondary">P99</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {latencyPercentiles.map((lp) => (
                                    <tr key={lp.jobType} className="hover:bg-primary_hover">
                                        <td className="px-4 py-3 font-medium text-primary">{lp.jobType}</td>
                                        <td className="px-4 py-3 text-tertiary">{formatMs(lp.p50Ms)}</td>
                                        <td
                                            className={cx(
                                                "px-4 py-3",
                                                lp.p95Ms > P95_THRESHOLD_MS ? "font-medium text-warning-primary" : "text-tertiary",
                                            )}
                                        >
                                            {formatMs(lp.p95Ms)}
                                        </td>
                                        <td className="px-4 py-3 text-tertiary">{formatMs(lp.p99Ms)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Bridge Health Grid */}
            <section>
                <h4 className="mb-3 text-sm font-medium text-secondary">Bridge Health</h4>
                {bridgeHealth.length === 0 ? (
                    <p className="text-sm text-quaternary">No bridges registered.</p>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {bridgeHealth.map((bridge) => {
                            const statusColor = bridge.status === "Active" ? "success" : bridge.status === "Unhealthy" ? "error" : "gray";
                            return (
                                <div key={bridge.bridgeRegistrationId} className="space-y-2 rounded-lg border border-secondary p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-primary">{bridge.bridgeName}</span>
                                        <Badge color={statusColor} size="sm" type="pill-color">
                                            {bridge.status}
                                        </Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                        <span className="text-tertiary">Last heartbeat</span>
                                        <span className="text-primary">
                                            {bridge.lastHeartbeatAt ? new Date(bridge.lastHeartbeatAt).toLocaleTimeString() : "Never"}
                                        </span>
                                        <span className="text-tertiary">Jobs processed</span>
                                        <span className="text-primary">{bridge.jobsProcessedTotal.toLocaleString()}</span>
                                        <span className="text-tertiary">Avg time</span>
                                        <span className="text-primary">{formatMs(bridge.avgProcessingTimeMs)}</span>
                                    </div>
                                    {bridge.connectorHealthJson && <ConnectorHealthDisplay json={bridge.connectorHealthJson} />}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* Top Errors Table */}
            <section>
                <h4 className="mb-3 text-sm font-medium text-secondary">Top Errors</h4>
                {topErrors.length === 0 ? (
                    <p className="text-sm text-quaternary">No errors in this time window.</p>
                ) : (
                    <div className="overflow-x-auto rounded-lg border border-secondary">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-secondary bg-secondary_subtle">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-secondary">Error Code</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Category</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Sample Message</th>
                                    <th className="px-4 py-3 text-right font-medium text-secondary">Count</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {topErrors.map((err, i) => (
                                    <tr key={i} className="hover:bg-primary_hover">
                                        <td className="px-4 py-3 font-mono text-xs text-primary">{err.errorCode}</td>
                                        <td className="px-4 py-3">
                                            <Badge color="gray" size="sm" type="pill-color">
                                                {err.errorCategory}
                                            </Badge>
                                        </td>
                                        <td className="max-w-[300px] truncate px-4 py-3 text-tertiary" title={err.sampleMessage ?? undefined}>
                                            {err.sampleMessage ?? "\u2014"}
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium text-primary">{err.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Quarantined Jobs */}
            <section>
                <h4 className="mb-3 text-sm font-medium text-secondary">Quarantined Jobs</h4>
                <div className="rounded-lg border border-secondary p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-primary">
                                <span className="font-semibold">{pipelineHealth.failedCount}</span> failed
                                {pipelineHealth.canceledCount > 0 && (
                                    <>
                                        , <span className="font-semibold">{pipelineHealth.canceledCount}</span> canceled
                                    </>
                                )}
                            </p>
                            {pipelineHealth.retryExhaustedCount > 0 && (
                                <p className="text-xs text-error-primary">
                                    {pipelineHealth.retryExhaustedCount} have exhausted all retries
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            {requeueResult && <span className="text-xs text-success-primary">{requeueResult}</span>}
                            <Button
                                color="primary"
                                size="sm"
                                isDisabled={isRequeuing || pipelineHealth.failedCount === 0}
                                onClick={handleBulkRequeue}
                            >
                                {isRequeuing ? "Requeuing..." : "Requeue All Failed"}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
